import { useState } from 'react';
import { Button, Card, Group, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { addNote, listNotes } from '../api/gen';
import { useOrg } from '../api/org';
import { when } from '../lib/format';

/** What the team says to itself about a thread. Never sent, never drafted
 * from, never shown to the customer — and this panel is deliberately styled
 * unlike the reply box so nobody types the wrong thing in the wrong one. */
export function NotePanel({ conversationId }: { conversationId: string }) {
  const org = useOrg();
  const qc = useQueryClient();
  const [body, setBody] = useState('');

  const notes = useQuery({
    queryKey: ['notes', conversationId],
    queryFn: () => unwrap(listNotes({ path: { id: conversationId }, headers: org })),
  });

  const add = useMutation({
    mutationFn: () => unwrap(addNote({ path: { id: conversationId }, headers: org, body: { body } })),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: ['notes', conversationId] });
    },
  });

  return (
    <Card withBorder padding="sm" radius="md" bg="var(--mantine-color-yellow-light)">
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Internal notes
        </Text>
        {(notes.data?.notes ?? []).map((n) => (
          <div key={n.id}>
            <Text size="xs" c="dimmed">
              {when(n.createdAt)}
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {n.body}
            </Text>
          </div>
        ))}
        <Textarea
          autosize
          minRows={2}
          placeholder="Only the team sees this."
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button
            size="compact-xs"
            variant="light"
            color="yellow"
            disabled={!body.trim()}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Add note
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
